import { Injectable } from "@angular/core";
import { BehaviorSubject, Subscription, interval } from "rxjs";
import { takeWhile } from "rxjs/operators";
import { Firestore, doc, getDoc } from "@angular/fire/firestore";
import { TranslateService } from "@ngx-translate/core";
import { StoreRepositoryService } from "../store-repository.service";
import { AuthService } from "@core/auth.service";
import { Data } from "@angular/router";

@Injectable()
export class CardOfferService {
  // reactive state
  private promotionsSubject = new BehaviorSubject<any[]>([]);
  promotions$ = this.promotionsSubject.asObservable();

  private timerSubject = new BehaviorSubject<{ minutes: number; seconds: number }>({ minutes: 0, seconds: 0 });
  timer$ = this.timerSubject.asObservable();

  private processingStatesSubject = new BehaviorSubject<boolean[]>([]);
  processingStates$ = this.processingStatesSubject.asObservable();

  // internal state
  private cardOfferDetails: Data[] = [];
  private activeCardIndex: number[] = [];

  // countdown configuration
  expireInMin = 5;
  timeLeft: number = this.expireInMin * 60;
  expiry_in_minutes!: number;
  seconds!: number;

  // slider / carousel configuration
  slides: any;
  interval: any;
  autoSlide = true;
  speed = 500;

  private countdownSub?: Subscription;

  constructor(
    private firestore: Firestore,
    private storeService: StoreRepositoryService,
    private authService: AuthService,
    private translate: TranslateService,
  ) {}

  private async loadPromotions(userId: string, fallbackCurrency: string): Promise<{ promotions: Data[]; cardOfferDetails: Data[] }> {
    if (!userId) {
      this.authService.errorHandlerLogs("User ID is missing from localStorage.", null);
      throw new Error("User ID is missing from localStorage.");
    }

    const path = `users/${userId}`;
    const userDocRef = doc(this.firestore, path);

    const docSnapshot = await getDoc(userDocRef);
    if (!docSnapshot.exists()) {
      throw new Error(`No document found at path: ${path}`);
    }

    const data = docSnapshot.data() as Data;
    const languageCurrency = data?.language_currency || fallbackCurrency;
    if (!languageCurrency) {
      this.authService.errorHandlerLogs("Language currency is missing in user data.", null);
      throw new Error("Language currency is missing.");
    }

    const cardOfferData = await this.storeService.getCardOfferDetails(languageCurrency);
    const promotions = cardOfferData?.promotions || [];
    this.promotionsSubject.next(promotions);
    this.processingStatesSubject.next(Array(promotions.length).fill(false));

    this.cardOfferDetails = [];
    for (const promotion of promotions) {
      const offerId = promotion.offer_id;
      this.authService.getOfferDetail(offerId).subscribe({
        next: (res) => {
          this.cardOfferDetails.push(res);
        },
        error: (err) => {
          this.authService.errorHandlerLogs("getOfferDetail failed", err);
        },
      });
    }

    return {
      promotions,
      cardOfferDetails: this.cardOfferDetails,
    };
  }

  async fetchOrderData(
    userId: string,
    fallbackCurrency: string
  ): Promise<{ promotions: Data[]; cardOfferDetails: Data[]; successToast: string; failToast: string }> {
    try {
      const { promotions, cardOfferDetails } = await this.loadPromotions(userId, fallbackCurrency);

      let successToast = "";
      let failToast = "";
      await new Promise<void>((resolve) => {
        this.translate
          .get(["store-payment.suggested-fail-toast", "store-payment.suggested-success-toast"])
          .subscribe({
            next: (res) => {
              if (!res) {
                this.authService.errorHandlerLogs("No translations received.", null);
                resolve();
                return;
              }
              successToast = res["store-payment.suggested-success-toast"] || "";
              failToast = res["store-payment.suggested-fail-toast"] || "";
              resolve();
            },
            error: (err) => {
              this.authService.errorHandlerLogs("Error loading translations:", err);
              resolve();
            },
          });
      });

      return {
        promotions,
        cardOfferDetails,
        successToast,
        failToast,
      };
    } catch (error) {
      this.authService.errorHandlerLogs("Error occurred in fetchOrderData:", error);
      throw error;
    }
  }

  /**
   * Initialize card offer promotions for a given user.
   * Fetches user document to determine language currency, then loads promotions.
   */
  async initialize(userId: string, fallbackCurrency: string): Promise<void> {
    if (!userId) {
      this.authService.errorHandlerLogs("User ID is missing from initialize in CardOfferService.", null);
      return;
    }

    try {
      await this.loadPromotions(userId, fallbackCurrency);
      // Initialize countdown timer state
      this.timeLeft = this.expireInMin * 60;
      this.updateTimerState();

      // preload success / fail toasts for card offers
      this.translate
        .get(["store-payment.suggested-fail-toast", "store-payment.suggested-success-toast"])
        .subscribe({
          next: () => {
            // Intentionally left blank – component will request translated strings as needed.
          },
          error: (err) => {
            this.authService.errorHandlerLogs("Error loading translations in CardOfferService:", err);
          },
        });
    } catch (error) {
      this.authService.errorHandlerLogs("Error occurred in CardOfferService.initialize:", error);
      throw error;
    }
  }

  /**
   * Timer countdown logic using RxJS interval.
   */
  startCountdown(): void {
    // Ensure any existing subscription is cleaned up first
    this.countdownSub?.unsubscribe();

    this.countdownSub = interval(1000)
      .pipe(takeWhile(() => this.timeLeft > 0))
      .subscribe(() => {
        this.timeLeft--;
        this.updateTimerState();
      });
  }

  private updateTimerState(): void {
    this.expiry_in_minutes = Math.floor(this.timeLeft / 60);
    this.seconds = this.timeLeft % 60;

    this.timerSubject.next({
      minutes: this.expiry_in_minutes,
      seconds: this.seconds,
    });
  }

  /**
   * Whether a given card index should be disabled.
   */
  isCardDisabled(index: number): boolean {
    return this.activeCardIndex.includes(index);
  }

  /**
   * Initiate payment for a given card offer.
   * NOTE: Actual payment processing should be delegated to CheckoutPaymentService.
   * This method is a placeholder to wire into that service.
   */
  initiateCardOfferPayment(item: any, index: number): void {
    const promotions = this.promotionsSubject.getValue() || [];
    let processingStates = this.processingStatesSubject.getValue() || [];

    if (processingStates.length < promotions.length) {
      processingStates = Array(promotions.length).fill(false);
    }

    // Set the processing state to true for the clicked card
    processingStates[index] = true;
    this.processingStatesSubject.next(processingStates);

    // TODO: Delegate to CheckoutPaymentService when available
    // For now, simply reset the processing state after a brief delay.
    setTimeout(() => {
      const currentStates = this.processingStatesSubject.getValue() || [];
      if (index < currentStates.length) {
        currentStates[index] = false;
        this.processingStatesSubject.next([...currentStates]);
      }
    }, 2000);
  }

  /**
   * Clean up any running timers / subscriptions.
   */
  destroy(): void {
    this.countdownSub?.unsubscribe();
    this.countdownSub = undefined;

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}


